import {RoutineStatus, TriggerType} from 'graphql-client/__generated/graphqlGlobalTypes';

// eslint-disable-next-line camelcase
import {RoutinesQuery_routines_RoutineNode_messageBlocks as GqlMessageBlockGenerated} from './__generated/RoutinesQuery';

export {RoutineStatus, TriggerType};

export type GqlMessageBlock = GqlMessageBlockGenerated;

export interface TextMessageBlock {
  type: 'text';
  text: string;
}

export interface FileMessageBlock {
  type: 'file';
  external_file_id: string;
  filename: string;
  media_type: string;
}

export type MessageBlock = TextMessageBlock | FileMessageBlock;

export function gqlBlocksToMessageBlocks(blocks: Array<GqlMessageBlock>): Array<MessageBlock> {
  return blocks.map((block) => {
    if (block.__typename === 'DisplayTextBlockNode') {
      return {type: 'text' as const, text: block.text};
    }
    return {
      type: 'file' as const,
      external_file_id: block.externalFileId,
      filename: block.filename,
      media_type: block.mediaType,
    };
  });
}

// NOTE: trigger_config is stored as raw JSONB and passed through the GraphQL resolver as-is.
// Keys are snake_case in the DB. The creation flow will finalize the config schema.
export interface TimeTriggerConfig {
  timezone: string;
  run_time: string;
  freq_type: 'daily' | 'weekly' | 'monthly';
  recurrence_interval: number;
  weekly_days?: Array<number>;
}

export interface EventTriggerConfig {
  object_type: string;
  field: string;
  object_ids: Array<string>;
  to_values: Array<string>;
}

export interface Routine {
  id: string;
  name: string | null;
  description: string | null;
  prompt: string;
  agentType: string;
  messageBlocks: Array<MessageBlock>;
  triggerType: TriggerType;
  triggerConfig: TimeTriggerConfig | EventTriggerConfig;
  status: RoutineStatus;
  createdAt: string;
  modifiedAt: string;
  nextRunAt: string | null;
}

export interface AllowedTriggerField {
  field: string;
  displayName: string;
  allowedValues: Array<string>;
}

export interface AllowedEventTrigger {
  objectType: string;
  fields: Array<AllowedTriggerField>;
}

export interface UpdateRoutinePayload {
  name?: string;
  description?: string;
  messageBlocks?: Array<MessageBlock>;
  triggerConfig?: TimeTriggerConfig | EventTriggerConfig;
}

export interface CreateRoutinePayload {
  name: string;
  description?: string;
  messageBlocks: Array<MessageBlock>;
  agentType: string;
  triggerType: TriggerType;
  triggerConfig: TimeTriggerConfig | EventTriggerConfig;
}

export function extractPromptFromBlocks(blocks: Array<MessageBlock>): string {
  const textBlock = blocks.find((b): b is TextMessageBlock => b.type === 'text');
  return textBlock?.text ?? '';
}

export function extractFileBlocks(blocks: Array<MessageBlock>): Array<FileMessageBlock> {
  return blocks.filter((b): b is FileMessageBlock => b.type === 'file');
}

export function buildMessageBlocks(prompt: string, fileBlocks: Array<FileMessageBlock>): Array<MessageBlock> {
  const blocks: Array<MessageBlock> = [{type: 'text', text: prompt}];
  return blocks.concat(fileBlocks);
}
